import { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { qualifyingFactsheetOf } from '../../../utils/client-factsheet';
import { RECRUIT_SCORES_CHANGED_EVENT, readRecruitScores, summariseRecruitScores } from './recruitScore';
import { RECRUIT_PROFILE_CHANGED_EVENT } from './recruitProfile';
import { readClientNotes } from './clientNotesStore';
import { RECRUIT_STAGE_CHANGED_EVENT, readRecruitStage } from './recruitStage';
import type { RecruitSummary } from './recruitScore';
import type { RecruitStage } from './recruitStage';
import type { QueueClient } from '../../../types/client';

/**
 * List-level derivations for 招募模式 — the tab row and the four tiles, over the
 * WHOLE client array. The business equivalents live in ./clientListView and are
 * built the same way on purpose: one predicate map feeds both the figure and
 * the list it opens, so a tile reading 「3 名」 can never open a list of four.
 *
 * ⚠️ Kept in its own file rather than added to clientListView because every
 * input here is the adviser's recruitment notes (./recruitStage,
 * ./recruitScore), read through ./clientNotesStore rather than straight off
 * the client objects — so pending optimistic writes show before the list
 * re-reads.
 */

/**
 * The stages where a candidate is still being worked. Anything asking 「what is
 * the next move for this person」 — the 待評估 tile, the card's rating rule — is
 * scoped to these, because 暫緩 and 已入職 have both stopped being questions:
 * rating someone who has already joined is paperwork about a decision that was
 * taken, and it would crowd out a line on a card that only has three.
 */
export const RECRUIT_ACTIVE_STAGES = ['contact', 'talking', 'exam'] as const;

/**
 * 進行中 is `contact` + `talking` — the two stages that are actually being
 * worked. 未開始 and 待評估 are not tabs: they are tiles, and a tab and a tile
 * saying the same thing would be one idea with two controls.
 *
 * 暫緩 IS a tab and IS counted in 全部, which is the point of the word: an
 * adviser asking for 全部 wants every candidate, including the ones parked. The
 * 墳場 problem the design doc warns about (§ graveyard) is answered by 暫緩
 * being visible and countable, not by hiding it.
 */
export const RECRUIT_TABS = ['all', 'active', 'exam', 'paused'] as const;
export type RecruitTab = (typeof RECRUIT_TABS)[number];

export const RECRUIT_SORTS = ['default', 'score', 'quiz', 'unrated'] as const;
export type RecruitSort = (typeof RECRUIT_SORTS)[number];

export const RECRUIT_STAT_KEYS = ['priority', 'notStarted', 'unrated', 'stalled'] as const;
export type RecruitStatKey = (typeof RECRUIT_STAT_KEYS)[number];
export type RecruitStats = Record<RecruitStatKey, number>;

/** 停滯 — being worked, but nothing recorded for this long. */
export const RECRUIT_STALLED_DAYS = 14;

/** One candidate's locally stored recruitment state, read once per list render. */
export type RecruitFacts = {
  stage: RecruitStage | null;
  summary: RecruitSummary;
  quizCompleted?: boolean;
};
export type RecruitFactsMap = Map<string, RecruitFacts>;

/** Frozen identity for the gated-off case, so no memo below it ever churns. */
const EMPTY_FACTS: RecruitFactsMap = new Map();

/**
 * Reads the notes store ONCE for the whole list. The per-client hooks
 * (useRecruitStage / useRecruitScores) are right for the drawer, where there is
 * one person; here there are two hundred rows, and two hundred subscriptions
 * would be two hundred listeners for one shared store.
 */
export const recruitFactsOf = (clients: QueueClient[]): RecruitFactsMap => {
  const facts: RecruitFactsMap = new Map();
  (clients || []).forEach(client => {
    facts.set(client._id, {
      stage: readRecruitStage(client._id),
      summary: summariseRecruitScores(readRecruitScores(client._id)),
      quizCompleted: Boolean(readClientNotes(client._id)?.quizCompletedAt.career)
    });
  });
  return facts;
};

const factsFor = (facts: RecruitFactsMap, client: QueueClient): RecruitFacts =>
  facts.get(client._id) || {
    stage: null,
    summary: { rated: 0, total: null, tier: null, familyConcern: false },
    quizCompleted: false
  };

const TAB_PREDICATE: Record<RecruitTab, (facts: RecruitFacts) => boolean> = {
  all: () => true,
  active: ({ stage }) => stage === 'contact' || stage === 'talking',
  exam: ({ stage }) => stage === 'exam',
  paused: ({ stage }) => stage === 'paused'
};

export const filterRecruitsByTab = (clients: QueueClient[], tab: RecruitTab, facts: RecruitFactsMap): QueueClient[] =>
  clients.filter(client => TAB_PREDICATE[tab](factsFor(facts, client)));

export const recruitTabCountsOf = (clients: QueueClient[], facts: RecruitFactsMap): Record<RecruitTab, number> =>
  RECRUIT_TABS.reduce((counts, tab) => {
    counts[tab] = filterRecruitsByTab(clients, tab, facts).length;
    return counts;
  }, {} as Record<RecruitTab, number>);

export type RecruitStatsInput = {
  now?: moment.MomentInput;
  adviserEmail?: string;
};

/**
 * The four tiles, and the same rule about halves as the business row: two say
 * where the opportunity is (優先跟進, 考期 lives on a tab instead), two say what
 * has been left undone (未評估, 停滯). They are deliberately MUTUALLY EXCLUSIVE
 * where they would otherwise overlap:
 *
 *   未開始   stage is `notStarted` — whatever the rating says
 *   待評估   stage has STARTED but the five angles are not all rated
 *
 * Without that split the two tiles would open largely the same list — a
 * candidate nobody has begun usually has no rating either — and an adviser
 * comparing 「5 名」 with 「6 名」 would be comparing the same people twice.
 */
const statPredicatesOf = (
  input: RecruitStatsInput,
  facts: RecruitFactsMap
): Record<RecruitStatKey, (client: QueueClient) => boolean> => {
  const stalledBefore = moment(input.now).startOf('day').subtract(RECRUIT_STALLED_DAYS, 'days');
  return {
    // A top rating is only an opportunity while there is still a move to make.
    // 已入職 and 暫緩 are out for the reason RECRUIT_ACTIVE_STAGES gives —
    // without this, everyone rated 優先 who has since joined stayed on the
    // tile for good. 未開始 / unset stays IN: rated well and untouched is the
    // most expensive miss on the page (今日招募's rule 3).
    priority: client => {
      const { stage, summary } = factsFor(facts, client);
      return summary.tier === 'priority' && stage !== 'joined' && stage !== 'paused';
    },
    notStarted: client => {
      const { stage } = factsFor(facts, client);
      // An unset stage counts as 未開始: nothing has been recorded, which is
      // exactly what the tile is asking about. Saying otherwise would hide
      // every candidate nobody has touched — the ones it exists to surface.
      return stage === null || stage === 'notStarted';
    },
    unrated: client => {
      const { stage, summary } = factsFor(facts, client);
      const working = stage !== null && (RECRUIT_ACTIVE_STAGES as readonly string[]).includes(stage);
      return working && summary.total === null;
    },
    stalled: client => {
      const { stage } = factsFor(facts, client);
      if (stage !== 'contact' && stage !== 'talking') return false;
      // No meeting on file is stalled by definition — the conversation is open
      // and nothing has been written down.
      const date = qualifyingFactsheetOf(client, input.adviserEmail)?.latestMeetingDate;
      if (!date) return true;
      const latest = moment(date).startOf('day');
      return !latest.isValid() || latest.isBefore(stalledBefore);
    }
  };
};

export const recruitStatsOf = (
  clients: QueueClient[],
  facts: RecruitFactsMap,
  input: RecruitStatsInput = {}
): RecruitStats => {
  const predicates = statPredicatesOf(input, facts);
  return RECRUIT_STAT_KEYS.reduce((stats, key) => {
    stats[key] = (clients || []).filter(predicates[key]).length;
    return stats;
  }, {} as RecruitStats);
};

/**
 * Narrows to the candidates behind one tile. Applied INSIDE the current tab
 * rather than replacing it (the caller filters by tab first), so a tile is a
 * narrowing like the search box — not a fifth tab in disguise.
 */
export const filterRecruitsByStat = (
  clients: QueueClient[],
  stat: RecruitStatKey | null,
  facts: RecruitFactsMap,
  input: RecruitStatsInput = {}
): QueueClient[] => {
  if (!stat) return clients;
  return clients.filter(statPredicatesOf(input, facts)[stat]);
};

/**
 * The list's view of the local store, rebuilt whenever anything the recruitment
 * surfaces read is written. One listener per store for the whole page: the
 * drawer writes, this re-reads, and the tiles, tab counts and 今日招募 move
 * without a reload.
 *
 * ⚠️ Every signal, not just the two this map holds. 今日招募 also reads the
 * concern chips (./recruitProfile) and the career quiz (`quizCompletedAt.career`),
 * and a reader that listens to fewer stores than it reads goes quietly stale:
 * the adviser adds 家人反對 in the drawer, closes it, and the card still shows
 * the three lines it had before.
 *
 * `enabled` is the campaign-code gate. Without it nothing downstream is ever
 * rendered, and reading the store for every client in the book would be work
 * done for a feature this adviser cannot see.
 */
export const useRecruitFacts = (clients: QueueClient[], enabled = true): RecruitFactsMap => {
  const [version, setVersion] = useState(0);

  useEffect(() => {
    const onChange = () => setVersion(value => value + 1);
    // The career quiz's signal is `quizCompletedAt.career`, which arrives with
    // the notes — so the notes event (all three of these) covers it too.
    const events = [RECRUIT_STAGE_CHANGED_EVENT, RECRUIT_SCORES_CHANGED_EVENT, RECRUIT_PROFILE_CHANGED_EVENT];
    events.forEach(event => window.addEventListener(event, onChange));
    return () => events.forEach(event => window.removeEventListener(event, onChange));
  }, []);

  // Re-read only when the array identity changes or a write announced itself.
  // The caller passes the CANDIDATES, not the whole book: a store keyed by
  // client id has nothing to say about the other few hundred, and reading it
  // for them is wasted work on every refresh.
  return useMemo(() => (enabled ? recruitFactsOf(clients) : EMPTY_FACTS), [clients, version, enabled]);
};

/**
 * 招募模式專用排序與篩選:
 *   default: 新→舊 (保持伺服器原順序，最新候選人最前)
 *   score: 評估分高→低 (總分 100-20 由高到低排序，未評估者置底，同分保持原順序)
 *   quiz: 已做職涯測驗 (篩選出已完成職涯測驗的候選人)
 *   unrated: 未評估 (篩選出尚未完成評估的候選人，即 summary.total === null)
 */
export const sortAndFilterRecruitsBySort = (
  clients: QueueClient[],
  sort: RecruitSort,
  facts: RecruitFactsMap
): QueueClient[] => {
  const list = clients || [];
  if (sort === 'quiz') {
    return list.filter(client => Boolean(factsFor(facts, client).quizCompleted));
  }
  if (sort === 'unrated') {
    return list.filter(client => factsFor(facts, client).summary.total === null);
  }
  if (sort === 'score') {
    const scoreOf = (client: QueueClient): number => {
      const total = factsFor(facts, client).summary.total;
      return total !== null ? total : -1;
    };
    return [...list].sort((left, right) => scoreOf(right) - scoreOf(left));
  }
  return list;
};
